import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getUser } from '@services/auth/AuthService';

export type AuthState = {
  user: string;
  isLoadingUser: boolean;
  isExpiredUser: boolean;
  isErrorUser: boolean;
};

export const initialAuthState: AuthState = {
  user: '',
  isLoadingUser: false,
  isExpiredUser: false,
  isErrorUser: false,
};

export const setUser = createAsyncThunk('auth/setUser', async (): Promise<string> => {
  try {
    const user = await getUser();
    if (user != null && !user.expired) {
      return JSON.stringify(user);
    } else {
      throw new Error('User not found or expired');
    }
  } catch (err) {
    throw new Error('User not found or expired');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    storeUser(state, action: PayloadAction<string>) {
      state.user = action.payload;
      state.isLoadingUser = false;
      state.isExpiredUser = false;
      state.isErrorUser = false;
    },
    loadingUser(state) {
      state.user = '';
      state.isLoadingUser = true;
      state.isExpiredUser = true;
      state.isErrorUser = false;
    },
    userExpired(state) {
      state.user = '';
      state.isLoadingUser = false;
      state.isExpiredUser = true;
      state.isErrorUser = false;
    },
    storeUserError(state) {
      state.user = '';
      state.isLoadingUser = false;
      state.isExpiredUser = true;
      state.isErrorUser = true;
    },
    userSignedOut(state) {
      state.user = '';
      state.isLoadingUser = false;
      state.isExpiredUser = true;
      state.isErrorUser = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setUser.pending, (state) => {
      state.isLoadingUser = true;
    });
    builder.addCase(setUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoadingUser = false;
      state.isExpiredUser = false;
      state.isErrorUser = false;
    });
    builder.addCase(setUser.rejected, (state) => {
      state.isLoadingUser = false;
      state.isExpiredUser = true;
      state.isErrorUser = true;
    });
  },
});

export const { storeUser, loadingUser, userExpired, storeUserError, userSignedOut } =
  authSlice.actions;
export default authSlice.reducer;
